import { useEffect, useState } from "react";
import { isValidPhoneNumber, parsePhoneNumberFromString } from "libphonenumber-js";
import { useDebounce } from "./useDebounce";
import apiFetch from "../../api/apiFetch";

// Only worth checking once it's a real, complete number — avoids firing a
// request on every keystroke of an obviously-incomplete number, and ensures
// this compares the same canonical E.164 form the backend ends up storing.
const normalizePhone = (phone: string): string =>
  isValidPhoneNumber(phone) ? parsePhoneNumberFromString(phone)!.number : "";

export function usePhoneCheck(phone: string) {
  const [phoneExists, setPhoneExists] = useState(false);
  const [checkingPhone, setCheckingPhone] = useState(false);

  const debouncedPhone = useDebounce(normalizePhone(phone), 500);

  useEffect(() => {
    if (!debouncedPhone) {
      setPhoneExists(false);
      setCheckingPhone(false);
      return;
    }

    let cancelled = false;

    const checkPhone = async () => {
      try {
        setCheckingPhone(true);

        const res = await apiFetch("/auth/check-phone", {
          method: "POST",
          auth: false,
          body: JSON.stringify({ phone: debouncedPhone }),
        });

        if (!cancelled) {
          setPhoneExists(Boolean(res.exists));
        }
      } catch {
        if (!cancelled) {
          setPhoneExists(false);
        }
      } finally {
        if (!cancelled) {
          setCheckingPhone(false);
        }
      }
    };

    checkPhone();

    return () => {
      cancelled = true;
    };
  }, [debouncedPhone]);

  return {
    phoneExists,
    checkingPhone,
  };
}
